import React, { useState, useEffect, useMemo } from 'react';
import { invoicesAPI } from '../lib/api';
import { AppDrawer } from './ui/AppDrawer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { CreditCard, Banknote, FileCheck, MoreHorizontal, Loader2, AlertTriangle, CheckCircle2, DollarSign, Send } from 'lucide-react';
import { toast } from 'sonner';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: Banknote, description: 'Record cash payment' },
  { value: 'check', label: 'Check', icon: FileCheck, description: 'Record check payment' },
  { value: 'credit_card', label: 'Credit Card', icon: CreditCard, description: 'Send payment link to customer' },
  { value: 'other', label: 'Other', icon: MoreHorizontal, description: 'Record other payment type' },
];

const OTHER_SUBTYPES = [
  { value: 'homeowner_financing', label: 'Homeowner Financing' },
  { value: 'warranty_work', label: 'Warranty Work' },
  { value: 'e_transfer', label: 'E-Transfer' },
  { value: 'other_cc_processor', label: 'Other Credit Card Processor' },
  { value: 'other', label: 'Other Payment Type' },
];

export default function TakePaymentDrawer({ open, onClose, invoices, jobId, onPaymentRecorded, customer }) {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [otherSubtype, setOtherSubtype] = useState('');
  const [note, setNote] = useState('');
  const [emailReceipt, setEmailReceipt] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  // Filter to invoices with a balance
  const payableInvoices = useMemo(() =>
    (invoices || []).filter(inv =>
      inv.status !== 'void' && inv.status !== 'paid' && Number(inv.balance_due || inv.total || 0) > 0
    ), [invoices]);

  // Auto-select when one invoice
  useEffect(() => {
    if (open) {
      if (payableInvoices.length === 1) {
        setSelectedInvoiceId(payableInvoices[0].id);
      } else {
        setSelectedInvoiceId('');
      }
      setAmount('');
      setPaymentMethod('');
      setOtherSubtype('');
      setNote('');
      setEmailReceipt(customer?.billing_email || '');
      setNotifyCustomer(false);
    }
  }, [open, payableInvoices, customer?.billing_email]);

  // Prefill amount from selected invoice balance
  useEffect(() => {
    if (selectedInvoiceId && !amount) {
      const inv = payableInvoices.find(i => i.id === selectedInvoiceId);
      if (inv) {
        setAmount(String(Number(inv.balance_due ?? inv.total ?? 0).toFixed(2)));
      }
    }
  }, [selectedInvoiceId, payableInvoices, amount]);

  const selectedInvoice = payableInvoices.find(i => i.id === selectedInvoiceId);

  const canSubmit = selectedInvoiceId && paymentMethod && Number(amount) > 0;
  const isCardMethod = paymentMethod === 'credit_card';

  const handleRecordPayment = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      // Map the method to what the backend expects
      let backendMethod = paymentMethod;
      if (paymentMethod === 'other') {
        backendMethod = 'other';
      }
      // credit_card uses a different flow (send payment link)

      await invoicesAPI.addPayment(selectedInvoiceId, {
        amount: parseFloat(amount),
        payment_method: backendMethod,
        notes: buildNotes(),
      });
      toast.success('Payment recorded');
      onPaymentRecorded?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleSendPaymentLink = async () => {
    if (!selectedInvoiceId) return;
    setSendingLink(true);
    try {
      // Send the invoice email with payment link
      await invoicesAPI.send(selectedInvoiceId, {
        to_email: emailReceipt || undefined,
      });
      toast.success('Payment link sent to customer');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send payment link');
    } finally {
      setSendingLink(false);
    }
  };

  const buildNotes = () => {
    const parts = [];
    if (paymentMethod === 'other' && otherSubtype) {
      const label = OTHER_SUBTYPES.find(s => s.value === otherSubtype)?.label || otherSubtype;
      parts.push(`Type: ${label}`);
    }
    if (note) parts.push(note);
    return parts.join(' | ') || undefined;
  };

  const noPayableInvoices = payableInvoices.length === 0;

  return (
    <AppDrawer
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      title="Take Payment"
      description={selectedInvoice ? `Invoice #${selectedInvoice.invoice_number} — $${Number(selectedInvoice.balance_due ?? selectedInvoice.total ?? 0).toFixed(2)} due` : 'Record a payment against a job invoice'}
      footer={
        noPayableInvoices ? (
          <Button variant="outline" onClick={onClose} data-testid="payment-drawer-close">Close</Button>
        ) : isCardMethod ? (
          <>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleSendPaymentLink}
              disabled={!selectedInvoiceId || sendingLink}
              data-testid="send-payment-link-btn"
            >
              {sendingLink ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Payment Link
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleRecordPayment}
              disabled={!canSubmit || saving}
              data-testid="record-payment-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
              Record Payment
            </Button>
          </>
        )
      }
    >
      <div className="space-y-5" data-testid="take-payment-drawer">
        {/* Guard: No payable invoices */}
        {noPayableInvoices ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="no-payable-invoices">
            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="font-medium text-sm">No invoices with a balance due</p>
              <p className="text-xs text-muted-foreground mt-1">
                Generate an invoice first, then come back to take payment.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Invoice Selector */}
            {payableInvoices.length > 1 ? (
              <div>
                <Label className="text-xs font-medium">Invoice</Label>
                <Select value={selectedInvoiceId} onValueChange={(v) => { setSelectedInvoiceId(v); setAmount(''); }}>
                  <SelectTrigger data-testid="invoice-selector">
                    <SelectValue placeholder="Select an invoice..." />
                  </SelectTrigger>
                  <SelectContent>
                    {payableInvoices.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>
                        #{inv.invoice_number} — ${Number(inv.balance_due ?? inv.total ?? 0).toFixed(2)} due ({inv.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : payableInvoices.length === 1 && (
              <div className="flex items-center gap-2 rounded-lg border p-3 bg-muted/30" data-testid="single-invoice-badge">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">Invoice #{payableInvoices[0].invoice_number}</p>
                  <p className="text-xs text-muted-foreground">
                    Balance: ${Number(payableInvoices[0].balance_due ?? payableInvoices[0].total ?? 0).toFixed(2)}
                  </p>
                </div>
              </div>
            )}

            {/* Payment Method */}
            {selectedInvoiceId && (
              <div>
                <Label className="text-xs font-medium mb-2 block">Payment Method</Label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map(m => {
                    const Icon = m.icon;
                    const selected = paymentMethod === m.value;
                    return (
                      <button
                        key={m.value}
                        onClick={() => { setPaymentMethod(m.value); setOtherSubtype(''); }}
                        className={`flex items-center gap-2.5 p-3 rounded-lg border text-left transition-all text-sm ${
                          selected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                            : 'border-border hover:border-primary/30 hover:bg-muted/30'
                        }`}
                        data-testid={`payment-method-${m.value}`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span className={selected ? 'font-medium' : ''}>{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Credit Card - info banner */}
            {isCardMethod && selectedInvoiceId && (
              <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-3 space-y-1.5" data-testid="card-payment-info">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300 flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4" /> Credit Card Payment
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  This will send the customer an invoice email with a secure Stripe payment link.
                  They can pay online with their credit card, debit card, or ACH if enabled.
                </p>
              </div>
            )}

            {/* Other subtype selector */}
            {paymentMethod === 'other' && (
              <div>
                <Label className="text-xs font-medium">Payment Type</Label>
                <Select value={otherSubtype} onValueChange={setOtherSubtype}>
                  <SelectTrigger data-testid="other-subtype-selector">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {OTHER_SUBTYPES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Amount — not shown for card (full balance sent in link) */}
            {paymentMethod && !isCardMethod && (
              <div>
                <Label className="text-xs font-medium">Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={selectedInvoice ? Number(selectedInvoice.balance_due ?? selectedInvoice.total ?? 999999) : undefined}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  data-testid="payment-amount-input"
                />
                {selectedInvoice && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Balance due: ${Number(selectedInvoice.balance_due ?? selectedInvoice.total ?? 0).toFixed(2)}
                  </p>
                )}
              </div>
            )}

            {/* Email receipt to */}
            {paymentMethod && (
              <div>
                <Label className="text-xs font-medium">Email Receipt To</Label>
                <Input
                  type="email"
                  value={emailReceipt}
                  onChange={(e) => setEmailReceipt(e.target.value)}
                  placeholder="customer@example.com"
                  data-testid="payment-email-input"
                />
              </div>
            )}

            {/* Payment Note */}
            {paymentMethod && !isCardMethod && (
              <div>
                <Label className="text-xs font-medium">Payment Note</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note (e.g., check #, reference)..."
                  rows={2}
                  className="text-sm"
                  data-testid="payment-note-input"
                />
              </div>
            )}

            {/* Notify customer checkbox */}
            {paymentMethod && !isCardMethod && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="notify-customer"
                  checked={notifyCustomer}
                  onCheckedChange={setNotifyCustomer}
                  data-testid="notify-customer-checkbox"
                />
                <label htmlFor="notify-customer" className="text-xs text-muted-foreground cursor-pointer">
                  Notify customer of payment received
                </label>
              </div>
            )}
          </>
        )}
      </div>
    </AppDrawer>
  );
}
